/**
 * Refunds and the corrections that fix them — `/v1/admin/refunds`, `/v1/admin/adjustments`.
 *
 * **Reads are wider than writes.** Support reads: "has her refund gone out?" is most of what they
 * are asked. Raising a refund is money leaving on somebody's judgement rather than on a policy
 * window, and the server enforces that separately — this file does not decide it.
 *
 * **Approving an adjustment invalidates the queue and the refunds list.** A posted adjustment
 * changes what a booking's money looks like, and a console that refreshed one and not the other
 * would show two screens disagreeing about the same correction.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type RefundRow = components['schemas']['RefundRow'];
export type AdminRefundRow = components['schemas']['AdminRefundRow'];
export type RefundLineRow = components['schemas']['RefundLineRow'];
export type AdjustmentRow = components['schemas']['AdjustmentRow'];
type AdminRefundPage = components['schemas']['AdminRefundPage'];
type AdjustmentPage = components['schemas']['AdjustmentPage'];
export type ManualRefundRequest = components['schemas']['ManualRefundRequest'];
export type AdjustmentCreate = components['schemas']['AdjustmentCreate'];

export interface RefundQuery {
  status?: string;
  booking_id?: number;
  reason?: string;
  initiated_by?: string;
  /** IST days, both inclusive. */
  date_from?: string;
  date_to?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

/** Only what the operator set. An empty `status=` would narrow to nothing rather than to all. */
export function refundParams(query: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export function useRefunds(query: RefundQuery) {
  const params = refundParams(query as Record<string, unknown>);
  return useQuery({
    queryKey: keys.refunds.list(query as Readonly<Record<string, unknown>>),
    queryFn: () => authedClient.request<AdminRefundPage>(`/v1/admin/refunds?${params}`),
  });
}

export function useAdjustments(status: string) {
  return useQuery({
    queryKey: keys.refunds.adjustments(status),
    queryFn: () =>
      authedClient.request<AdjustmentPage>(
        `/v1/admin/adjustments?${refundParams({ status })}`,
      ),
  });
}

function useAdjustmentAction(action: 'approve' | 'reject') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      authedClient.request<AdjustmentRow>(`/v1/admin/adjustments/${id}/${action}`, {
        method: 'POST',
        ...(reason === undefined ? {} : { body: { reason } }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.refunds.all() });
    },
  });
}

export const useApproveAdjustment = () => useAdjustmentAction('approve');
export const useRejectAdjustment = () => useAdjustmentAction('reject');

/**
 * The maker's half: a draft, then a separate submit. Two calls rather than one because a draft
 * is the operator's to reread — the server holds it unseen by approvers until it is sent.
 */
export function useCreateAdjustment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdjustmentCreate) =>
      authedClient.request<AdjustmentRow>('/v1/admin/adjustments', { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.refunds.all() });
    },
  });
}

export function useSubmitAdjustment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      authedClient.request<AdjustmentRow>(`/v1/admin/adjustments/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.refunds.all() });
    },
  });
}

/**
 * A refund raised by hand — goodwill, a pricing error, a dispute settled in the devotee's favour.
 *
 * The booking is named by its `BMP…` code because finance holds this route but not the bookings
 * read; the server trims and upper-cases what was pasted.
 */
export function useRaiseRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualRefundRequest) =>
      authedClient.request<RefundRow>('/v1/admin/refunds', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.refunds.all() });
    },
  });
}

/**
 * Send a failed refund again (doc 05-admin/17 §2.1, the "Stuck at gateway" retry). Back to
 * `initiated` under a new gateway key; a second press finds it no longer `failed` and is refused,
 * so two clicks cannot queue two sends.
 */
export function useRetryRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; reason: string }) =>
      authedClient.request<RefundRow>(`/v1/admin/refunds/${args.id}/retry`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.refunds.all() }),
  });
}
